import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Send, MessageSquare } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useState } from "react";
import { formatDistanceToNow } from "date-fns";

interface GroupMessagingProps {
  challengeId: string;
}

export function GroupMessaging({ challengeId }: GroupMessagingProps) {
  const queryClient = useQueryClient();
  const [message, setMessage] = useState("");

  const { data: user } = useQuery({
    queryKey: ['user'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      return user;
    },
  });

  const { data: messages } = useQuery({
    queryKey: ['group-messages', challengeId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('group_messages')
        .select('*')
        .eq('challenge_id', challengeId)
        .order('created_at', { ascending: true });

      if (error) throw error;

      // Get sender profiles
      const userIds = [...new Set(data.map(m => m.sender_id))];
      if (userIds.length === 0) return [];

      const { data: profiles } = await supabase
        .from('profiles')
        .select('user_id, display_name')
        .in('user_id', userIds);

      const profileMap = new Map(profiles?.map(p => [p.user_id, p]) || []);

      return data.map(m => ({
        ...m,
        sender_profile: profileMap.get(m.sender_id),
      }));
    },
  });

  const sendMessageMutation = useMutation({
    mutationFn: async () => {
      if (!user?.id || !message.trim()) return;

      const { error } = await supabase
        .from('group_messages')
        .insert({
          challenge_id: challengeId,
          sender_id: user.id,
          message: message.trim(),
        });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['group-messages'] });
      setMessage("");
    },
    onError: () => {
      toast.error("Failed to send message");
    },
  });

  return (
    <Card className="flex flex-col h-[500px]">
      <CardHeader className="border-b">
        <CardTitle className="flex items-center gap-2">
          <MessageSquare className="h-5 w-5 text-accent" />
          Group Chat
        </CardTitle>
      </CardHeader>
      <ScrollArea className="flex-1 p-4">
        <div className="space-y-4">
          {messages && messages.length > 0 ? (
            messages.map((msg) => {
              const isMe = msg.sender_id === user?.id;
              return (
                <div key={msg.id} className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[70%] ${isMe ? '' : 'flex gap-2'}`}>
                    {!isMe && (
                      <Avatar className="h-8 w-8">
                        <AvatarFallback className="text-xs">
                          {msg.sender_profile?.display_name?.[0] || 'U'}
                        </AvatarFallback>
                      </Avatar>
                    )}
                    <div>
                      {!isMe && (
                        <p className="text-xs text-muted-foreground mb-1">
                          {msg.sender_profile?.display_name || 'User'}
                        </p>
                      )}
                      <div className={`rounded-lg p-3 ${
                        isMe ? 'bg-primary text-primary-foreground' : 'bg-muted'
                      }`}>
                        <p className="text-sm">{msg.message}</p>
                        <p className="text-xs opacity-70 mt-1">
                          {formatDistanceToNow(new Date(msg.created_at), { addSuffix: true })}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="text-center py-8">
              <MessageSquare className="h-12 w-12 mx-auto text-muted-foreground/50 mb-3" />
              <p className="text-sm text-muted-foreground">
                No messages yet. Start the conversation!
              </p>
            </div>
          )}
        </div>
      </ScrollArea>
      <CardContent className="border-t p-4">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            sendMessageMutation.mutate();
          }}
          className="flex gap-2"
        >
          <Input
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Type a message..."
            disabled={sendMessageMutation.isPending}
          />
          <Button type="submit" disabled={!message.trim() || sendMessageMutation.isPending}>
            <Send className="h-4 w-4" />
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
